/**
 * What a hit feels like.
 *
 * The simulation already knew everything that happens in a fight — it just never
 * told anyone. An orb landed, a number in the corner changed, and that was the
 * whole report. This module turns those facts into the things a player actually
 * reads without looking: the screen moving, a number leaving the impact, a ring
 * spreading from it, a line in the feed, the room hearing about a kill.
 *
 * It owns no canvas and no DOM. Events go in, state advances by dt, and the
 * renderer reads it out — so the whole of game feel can be tested by asserting on
 * numbers rather than by looking at it.
 *
 * Nothing here is simulation. It is safe to be wrong, to be skipped on a slow
 * frame, or to be switched off entirely for someone who asked for less motion.
 */

export type FloatingNumber = {
  active: boolean;
  x: number;
  y: number;
  /** Upward drift, world units per second. Damped so the number settles as it fades. */
  vy: number;
  vx: number;
  life: number;
  maxLife: number;
  text: string;
  color: string;
  /** Bigger and outlined: reserved for what the player must not miss. */
  weight: "normal" | "loud";
};

export type ImpactRing = {
  active: boolean;
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  life: number;
  maxLife: number;
  color: string;
  width: number;
};

export type FeedEntry = {
  id: number;
  attacker: string;
  attackerColor: string;
  victim: string;
  victimColor: string;
  /** Whether the local player is either end of it — those lines are highlighted. */
  involved: boolean;
  life: number;
};

export type StreakBanner = { id: number; count: number; life: number };

export type FeedbackState = {
  /**
   * Screen shake, as a single 0..1 quantity rather than an amplitude and a timer.
   *
   * Curving it on the way out is the whole trick: two small hits add up to
   * something you barely feel, and a kill at close range is violent. An amplitude
   * that decayed linearly made every event feel identical.
   */
  trauma: number;
  /** White-out over the whole viewport when the local core is the one struck. */
  flash: number;
  flashColor: string;
  numbers: FloatingNumber[];
  rings: ImpactRing[];
  feed: FeedEntry[];
  /** Consecutive kills without dying. Reset by death, not by time. */
  streak: number;
  banner: StreakBanner | null;
  nextId: number;
};

/** Caps, so a twenty-player brawl cannot grow these without bound. */
const MAX_NUMBERS = 28;
const MAX_RINGS = 14;
export const MAX_FEED = 5;

export const MAX_SHAKE_PIXELS = 30;
/**
 * Squaring the trauma is the usual advice and it was wrong for these numbers.
 * A steal is 0.3 trauma, and 0.3² is 0.09 — under three pixels, which is nothing at
 * all. At 1.5 the ordering still holds (a hit twice as hard moves the screen more
 * than twice as far) but the common, quiet events survive. Measured peaks: 3.7px for
 * a steal, 6.4 for being robbed, 9.8 for a kill, 24.9 for dying, over 0.2s to 0.65s.
 */
const TRAUMA_CURVE = 1.5;
const TRAUMA_DECAY_PER_SECOND = 1.55;
const FLASH_DECAY_PER_SECOND = 3.4;
/**
 * Measured, not guessed: a twenty-bot room produces about 1.3 kills a second, so
 * five slots turn over roughly every four seconds anyway. A longer life would only
 * mean entries were pushed out before they expired, which reads as a jumpier list.
 */
const FEED_SECONDS = 4;
const BANNER_SECONDS = 2.2;

/** How hard each thing hits, before the curve. Death is the loudest thing that happens. */
export const TRAUMA = {
  /** Your orb landed on someone. */
  steal: 0.3,
  /** Somebody's orb landed on you. */
  robbed: 0.42,
  /** You broke a core. */
  kill: 0.55,
  /** Your core broke. */
  death: 1,
  /** Your shield ate an incoming orb — a good outcome, so a light one. */
  block: 0.22,
  /** A ring finished charging. */
  charge: 0.18,
};

/** The milestones worth interrupting the screen for. Anything between them is quiet. */
export const STREAK_MILESTONES = [2, 3, 5, 7, 10];

export function createFeedback(): FeedbackState {
  return {
    trauma: 0,
    flash: 0,
    flashColor: "255,255,255",
    numbers: [],
    rings: [],
    feed: [],
    streak: 0,
    banner: null,
    nextId: 1,
  };
}

export function addTrauma(state: FeedbackState, amount: number) {
  // Additive and clamped, so simultaneous hits stack up to the ceiling instead of
  // the last one overwriting the worst one.
  state.trauma = Math.min(1, state.trauma + Math.max(0, amount));
}

export function addFlash(state: FeedbackState, amount: number, color = "255,255,255") {
  if (amount <= state.flash) return;
  state.flash = Math.min(1, amount);
  state.flashColor = color;
}

/**
 * Where the camera should be this frame.
 *
 * Two incommensurable frequencies per axis rather than a random number: random
 * jitter at 60fps is visual noise, while a sum of sines is a shake — it has a
 * direction for a few frames at a time, which is what a real impact does. The
 * seeds differ per axis so it never travels along a diagonal.
 */
export function shakeOffset(state: FeedbackState, time: number) {
  if (state.trauma <= 0) return { x: 0, y: 0 };
  const amount = Math.pow(state.trauma, TRAUMA_CURVE) * MAX_SHAKE_PIXELS;
  return {
    x: amount * (Math.sin(time * 47.3) * 0.6 + Math.sin(time * 23.1 + 1.7) * 0.4),
    y: amount * (Math.sin(time * 41.7 + 2.4) * 0.6 + Math.sin(time * 29.3 + 0.6) * 0.4),
  };
}

function freeSlot<T extends { active: boolean; life: number }>(list: T[], cap: number, make: () => T): T | null {
  for (const item of list) if (!item.active) return item;
  if (list.length >= cap) {
    // Full and all live: the oldest is the one nearest the end of its life, and
    // stealing it is better than dropping the event the player just caused.
    let oldest = list[0];
    for (const item of list) if (item.life < oldest.life) oldest = item;
    return oldest;
  }
  const created = make();
  list.push(created);
  return created;
}

export function pushNumber(
  state: FeedbackState,
  options: { x: number; y: number; text: string; color: string; weight?: FloatingNumber["weight"] },
) {
  const slot = freeSlot<FloatingNumber>(state.numbers, MAX_NUMBERS, () => ({
    active: false, x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, text: "", color: "#fff", weight: "normal",
  }));
  if (!slot) return;
  const loud = options.weight === "loud";
  slot.active = true;
  slot.x = options.x;
  slot.y = options.y;
  // A small sideways drift keeps two numbers from the same spot legible.
  slot.vx = (state.nextId % 5 - 2) * 7;
  slot.vy = loud ? -74 : -58;
  slot.maxLife = loud ? 1.5 : 1.05;
  slot.life = slot.maxLife;
  slot.text = options.text;
  slot.color = options.color;
  slot.weight = options.weight ?? "normal";
  state.nextId += 1;
}

export function pushRing(
  state: FeedbackState,
  options: { x: number; y: number; color: string; maxRadius?: number; width?: number; seconds?: number },
) {
  const slot = freeSlot<ImpactRing>(state.rings, MAX_RINGS, () => ({
    active: false, x: 0, y: 0, radius: 0, maxRadius: 60, life: 0, maxLife: 1, color: "#fff", width: 3,
  }));
  if (!slot) return;
  slot.active = true;
  slot.x = options.x;
  slot.y = options.y;
  slot.radius = 0;
  slot.maxRadius = options.maxRadius ?? 70;
  slot.maxLife = options.seconds ?? 0.45;
  slot.life = slot.maxLife;
  slot.color = options.color;
  slot.width = options.width ?? 3;
}

export function pushFeed(
  state: FeedbackState,
  entry: Omit<FeedEntry, "id" | "life">,
) {
  state.feed.unshift({ ...entry, id: state.nextId, life: FEED_SECONDS });
  state.nextId += 1;
  if (state.feed.length > MAX_FEED) state.feed.length = MAX_FEED;
}

/**
 * Records a kill by the local player and returns the milestone reached, if any.
 * The caller decides what to say about it; this only counts.
 */
export function registerKill(state: FeedbackState): number | null {
  state.streak += 1;
  if (!STREAK_MILESTONES.includes(state.streak)) return null;
  state.banner = { id: state.nextId, count: state.streak, life: BANNER_SECONDS };
  state.nextId += 1;
  return state.streak;
}

/** A run ends where a streak ends. Called on death, and on leaving the arena. */
export function resetStreak(state: FeedbackState) {
  state.streak = 0;
  state.banner = null;
}

export function advanceFeedback(state: FeedbackState, dt: number) {
  const step = Math.max(0, Math.min(dt, 0.1));

  state.trauma = Math.max(0, state.trauma - TRAUMA_DECAY_PER_SECOND * step);
  state.flash = Math.max(0, state.flash - FLASH_DECAY_PER_SECOND * step);

  for (const number of state.numbers) {
    if (!number.active) continue;
    number.x += number.vx * step;
    number.y += number.vy * step;
    // Damped rather than linear: the number leaps off the impact and settles,
    // which reads as something being knocked loose.
    number.vy *= Math.pow(0.12, step);
    number.vx *= Math.pow(0.12, step);
    number.life -= step;
    if (number.life <= 0) number.active = false;
  }

  for (const ring of state.rings) {
    if (!ring.active) continue;
    ring.life -= step;
    if (ring.life <= 0) {
      ring.active = false;
      continue;
    }
    // Fast out, slow at the edge — an expanding shell, not a growing circle.
    const progress = 1 - ring.life / ring.maxLife;
    ring.radius = ring.maxRadius * (1 - Math.pow(1 - progress, 2.6));
  }

  for (const entry of state.feed) entry.life -= step;
  while (state.feed.length > 0 && state.feed[state.feed.length - 1].life <= 0) state.feed.pop();

  if (state.banner) {
    state.banner.life -= step;
    if (state.banner.life <= 0) state.banner = null;
  }
}
